/**
 * The Help & feedback panel's wire (SA-1310 over SA-1303's `/api/app-feedback`). The turn is an SSE
 * stream read through `fetch`; everything else goes through `apiClient`.
 */
import { ApiError, apiClient } from '@/services/apiClient'
import type { ChipId } from '@/services/feedbackContext/chips'
import { useAuthStore } from '@/stores/authStore'

const BASE = '/api/app-feedback'

export type AssessmentKind = 'problem' | 'idea' | 'question' | 'praise' | 'guidance'
export type SendKind = AssessmentKind

/** The latest navigation, without `kind` and `at`. */
export type PageInfo = Record<string, unknown> | null

export interface TurnBody {
  feedback_id?: string
  message: string
  spoken: boolean
  page: PageInfo
  context: unknown
}

/** A page of the help an answer read (SA-1314): a guide or page, an app page, or a template step. */
export interface HelpSource {
  slug: string
  title: string
  href: string | null
  kind: 'guide' | 'page' | 'map' | 'step'
}

export interface TurnAssessment {
  mode: 'answer' | 'report'
  kind: AssessmentKind
  summary: string | null
  attempting: string | null
  question: string | null
  reply: string
  /** On an answer: whether the help covered the question (SA-1313); absent from an older server. */
  found?: 'yes' | 'partly' | 'no' | null
  /** On an answer: the help it read, in the order it named them (SA-1314); absent from an older server. */
  sources?: HelpSource[]
  /** On a found answer: the task tour of the walkthrough section it drew on (SA-1316). */
  tour?: string | null
}

export interface SendBody {
  kind: SendKind
  summary: string | null
  attempting: string | null
  page: PageInfo
  context: unknown
  removed: ChipId[]
  resolved_by_guidance: boolean
}

export interface OneStepSendBody extends SendBody {
  message: string
  spoken: boolean
}

export interface ReportRead {
  id: string
  reference: string
  kind: AssessmentKind | null
  status: 'new' | 'triaged' | 'planned' | 'done' | 'not_planned'
  summary: string | null
  sent_at: string
}

/** `answers` and `voice` arrived with SA-1306; an older server omits them (read as on). */
export interface StatusRead {
  assistant: boolean
  answers?: boolean
  voice?: boolean
}

export function getStatus(): Promise<StatusRead> {
  return apiClient.get(`${BASE}/status`)
}

export function sendDraft(feedbackId: string, body: SendBody): Promise<ReportRead> {
  return apiClient.post(`${BASE}/${encodeURIComponent(feedbackId)}/send`, body)
}

export function sendNew(body: OneStepSendBody): Promise<ReportRead> {
  return apiClient.post(`${BASE}/send`, body)
}

export function listMine(): Promise<ReportRead[]> {
  return apiClient.get(`${BASE}/mine`)
}

// --- settings (SA-1306) -------------------------------------------------------------------------

export interface HelpFeedbackSettings {
  answers: boolean
  voice: boolean
  suggest_after_failure: boolean
  extra_instructions: string
}

export function getSettings(): Promise<HelpFeedbackSettings> {
  return apiClient.get(`${BASE}/settings`)
}

export function putSettings(body: HelpFeedbackSettings): Promise<HelpFeedbackSettings> {
  return apiClient.put(`${BASE}/settings`, body)
}

// --- admin (SA-1305) ----------------------------------------------------------------------------

export type ReportStatus = ReportRead['status']

export interface AdminReportRow extends ReportRead {
  attempting: string | null
  page_path: string | null
  sender_name: string | null
  resolved_by_guidance: boolean
  has_note: boolean
  /** A question sent from an answer the help lacked (SA-1314). */
  help_gap: boolean
}

export interface SenderFacet {
  id: string
  name: string
}

export interface AdminReportList {
  items: AdminReportRow[]
  total: number
  page: number
  per_page: number
  resolved_guidance_count: number
  senders: SenderFacet[]
}

export interface AdminMessage {
  role: 'user' | 'assistant'
  text: string
  answer: string | null
  question: string | null
  spoken: boolean
  at: string | null
  /** On an answer: whether the help held it, and what it drew on (SA-1314). */
  found?: 'yes' | 'partly' | 'no' | null
  sources?: HelpSource[]
}

export interface AdminReportDetail extends AdminReportRow {
  messages: AdminMessage[]
  page_info: Record<string, unknown> | null
  context: Record<string, unknown> | null
  removed: string[]
  staff_note: string | null
  staff_note_by_name: string | null
  staff_note_at: string | null
  updated_at: string
}

export interface AdminPatch {
  status?: ReportStatus
  staff_note?: string | null
  expected_updated_at?: string
}

export interface AdminListQuery {
  kind?: AssessmentKind
  status?: ReportStatus
  pagePath?: string
  user?: string
  resolvedGuidance?: boolean
  helpGap?: boolean
  page?: number
}

/** The list's path: only the keys that are set; `per_page` is never sent (the server's 50). */
export function adminListPath(q: AdminListQuery): string {
  const params = new URLSearchParams()
  if (q.kind) params.set('kind', q.kind)
  if (q.status) params.set('status', q.status)
  if (q.pagePath) params.set('page_path', q.pagePath)
  if (q.user) params.set('user', q.user)
  if (q.resolvedGuidance) params.set('resolved_guidance', 'true')
  if (q.helpGap) params.set('help_gap', 'true')
  if (q.page && q.page > 1) params.set('page', String(q.page))
  const query = params.toString()
  return query ? `${BASE}/admin?${query}` : `${BASE}/admin`
}

export function listAdminReports(q: AdminListQuery): Promise<AdminReportList> {
  return apiClient.get(adminListPath(q))
}

export function getAdminReport(id: string): Promise<AdminReportDetail> {
  return apiClient.get(`${BASE}/admin/${encodeURIComponent(id)}`)
}

/** PATCH through `apiClient.fetch` (it has no `patch`); a refusal is an `ApiError` with its detail. */
export async function patchAdminReport(id: string, patch: AdminPatch): Promise<AdminReportDetail> {
  const path = `${BASE}/admin/${encodeURIComponent(id)}`
  const response = await apiClient.fetch(path, { method: 'PATCH', body: JSON.stringify(patch) })
  if (!response.ok) {
    let detail: unknown = null
    try {
      const body = await response.json()
      detail = body && typeof body === 'object' && 'detail' in body ? (body as { detail: unknown }).detail : body
    } catch {
      // no JSON body: the status alone says what happened
    }
    throw new ApiError(`PATCH ${path} failed: ${response.statusText}`, response.status, detail)
  }
  return response.json()
}

/** Someone else changed the report since it was read. */
export function isFeedbackChanged(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && refusalCode(err) === 'feedback_changed'
}

/** A refusal's `detail.code`, else null. */
export function refusalCode(err: unknown): string | null {
  const detail = err instanceof ApiError ? err.detail : (err as { detail?: unknown } | null)?.detail
  if (detail && typeof detail === 'object' && 'code' in detail) {
    const code = (detail as { code: unknown }).code
    return typeof code === 'string' ? code : null
  }
  return null
}

/** A refused turn: the stream never started. */
export class TurnRefused extends Error {
  status: number
  detail: unknown
  constructor(status: number, detail: unknown) {
    super(`Turn refused: ${status}`)
    this.name = 'TurnRefused'
    this.status = status
    this.detail = detail
  }
}

export interface TurnCallbacks {
  onDraft?: (feedbackId: string) => void
  onToken?: (token: string) => void
  onTurn?: (turn: TurnAssessment) => void
  /** `assistant_failed` from the server, or `stream_broken` when the stream ended without a turn. */
  onError?: (code: string) => void
  signal?: AbortSignal
}

/**
 * Sends one turn and reads its stream. Resolves once the stream ends; rejects with `TurnRefused` when
 * the server refuses before streaming (its `detail.code` read by `refusalCode`).
 */
export async function streamTurn(body: TurnBody, callbacks: TurnCallbacks): Promise<void> {
  const token = useAuthStore.getState().accessToken
  const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'text/event-stream' }
  if (token) headers['Authorization'] = `Bearer ${token}`
  const response = await fetch(`${BASE}/turns`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    credentials: 'include',
    signal: callbacks.signal,
  })
  if (!response.ok || !response.body) {
    let detail: unknown = null
    try {
      const parsed = await response.json()
      detail = parsed && typeof parsed === 'object' && 'detail' in parsed ? parsed.detail : parsed
    } catch {
      // no JSON body
    }
    throw new TurnRefused(response.status, detail)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let event: string | null = null
  let ended = false

  const handle = (data: string) => {
    if (data === '[DONE]') {
      ended = true
      return
    }
    let parsed: unknown
    try {
      parsed = JSON.parse(data)
    } catch {
      return
    }
    const obj = parsed as Record<string, unknown>
    if (event === 'draft') {
      if (typeof obj.feedback_id === 'string') callbacks.onDraft?.(obj.feedback_id)
    } else if (event === 'turn') {
      ended = true
      callbacks.onTurn?.(obj as unknown as TurnAssessment)
    } else if (event === 'error') {
      ended = true
      callbacks.onError?.(typeof obj.code === 'string' ? obj.code : 'assistant_failed')
    } else if (obj.object === 'chat.completion.chunk') {
      const choices = obj.choices as Array<{ delta?: { content?: string } }> | undefined
      const content = choices?.[0]?.delta?.content
      if (content) callbacks.onToken?.(content)
    }
  }

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''
      for (const raw of lines) {
        const line = raw.replace(/\r$/, '')
        if (line === '') {
          event = null
          continue
        }
        if (line.startsWith(':')) continue
        if (line.startsWith('event:')) {
          event = line.slice(6).trim()
          continue
        }
        if (line.startsWith('data:')) handle(line.slice(5).trimStart())
      }
    }
    if (buffer.startsWith('data:')) handle(buffer.slice(5).trimStart())
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') return
    if (!ended) callbacks.onError?.('stream_broken')
    return
  }
  if (!ended) callbacks.onError?.('stream_broken')
}
